import type { BuilderContent } from '@builder.io/react'
import type { BuilderContent as BuilderContentSDK, Input } from '@builder.io/sdk'

import type { LocaleCode } from '@src/i18n/locales'
import { getIsServer } from '@src/utils/common.util'
import { transformExternalBODiLinkInText } from '@src/utils/externalBodiLink'
import { proxyImages } from '@src/utils/imageProxy.util'

export const getUserAttributes = ({
  urlPath,
  locale,
  searchParams,
}: {
  urlPath: string
  locale: string
  searchParams: any
}) => {
  return Object.entries(searchParams).reduce(
    (str, [p, val]) => {
      // ignore builder built-in parameters
      if (p.includes('builder')) {
        return str
      }
      return { ...str, [p]: `'${val}'` }
    },
    { urlPath, locale }
  )
}

export interface BuilderPreviewMode {
  edit: boolean
  live: boolean
  draft: boolean
}

const emptyBuildertState = {
  edit: false,
  live: false,
  draft: false,
}

const builderPreviewState = {
  edit: {
    ...emptyBuildertState,
    edit: true,
  },
  live: {
    ...emptyBuildertState,
    live: true,
  },
  draft: {
    ...emptyBuildertState,
    draft: true,
  },
}

/**
 * Isomorphic function to check if the current context is in preview mode
 *
 * @param searchParams (optional) - searchParams object if on server-side
 * @returns
 * ```
 * {
 *  edit: boolean
 *  live: boolean
 *  draft: boolean
 * }
 * ```
 */
export const getBuilderPreviewMode = (
  searchParams?: Record<string, any> | URLSearchParams | null
): BuilderPreviewMode => {
  const params = getIsServer()
    ? new URLSearchParams(searchParams ?? undefined)
    : new URLSearchParams(window.location.search)
  if (params.has('builder.frameEditing')) return builderPreviewState.edit
  else if (params.has('builder.preview')) return builderPreviewState.draft
  return builderPreviewState.live
}

export const filterOutBuilderProps = <T extends object>(props: T) => {
  const {
    builderState,
    builderBlock,
    actionName,
    addToCart,
    addItemsToCart,
    upsellToCart,
    revertUpsell,
    addPDPItemToCart,
    navigate,
    popoutVideo,
    openModal,
    selectSKU,
    addSelectedSKUsToCart,
    ...rest
  } = props as any

  return rest as Exclude<
    T,
    | 'builderState'
    | 'builderBlock'
    | 'actionName'
    | 'addToCart'
    | 'addItemsToCart'
    | 'upellToCart'
    | 'revertUpsell'
    | 'addPDPItemToCart'
    | 'navigate'
    | 'popoutVideo'
    | 'openModal'
    | 'selectSKU'
    | 'addSelectedSKUsToCart'
  >
}

/** Returns only the Builder attributes needed to style and manage a custom component. */
export const filterBuilderAttributes = (attributes: any = {}) => {
  return Object.entries(attributes).reduce((acc, [key, value]) => {
    if (['className', 'style', 'builder-id', 'data-testid'].includes(key))
      return { ...acc, [key]: value }
    return acc
  }, {}) as Record<'className' | 'style' | 'builder-id' | 'data-testid', any>
}

export type Spacing = 'standard' | 'none' | undefined

export const spacingBuilderInputs = {
  innerSpacing: {
    name: 'innerSpacing',
    type: 'text',
    enum: ['standard', 'none'],
    defaultValue: 'none',
  },
  outerSpacing: {
    name: 'outerSpacing',
    type: 'text',
    enum: ['standard', 'none'],
    defaultValue: 'none',
  },
}

export const getInnerSpacingClassNames = (spacing: Spacing) => {
  const innerSpacingConst: Record<string, string | undefined> = {
    standard: 'lg:py-20 py-12',
    none: 'py-0',
  }

  return innerSpacingConst[spacing as string] ?? ''
}

export const getOuterSpacingClassNames = (spacing: Spacing) => {
  const outerSpacingConst: Record<string, string | undefined> = {
    standard: 'lg:mb-20 mb-12',
    none: 'mb-0',
  }
  return outerSpacingConst[spacing as string] ?? ''
}

/**
 * This factory function returns a function that can be used to handle image changes in Builder.io.
 * When an image is changed, it generates a data URI for the new image and sets it to the specified property.
 *
 * The function is defined in a string and then converted to a function using `new Function`, this is because
 * Builder.io does not support functions with closures.
 **/
export function getOnChangeImageHandler(args: { srcProperty: string; dataUriProperty: string }) {
  const fn = `
  async function handler(options) {
    const srcImage = options?.get('${args.srcProperty}')
    const dataUrl = await generataDataURI(srcImage)
    options.set('${args.dataUriProperty}', dataUrl)
  }

  async function generataDataURI(url) {
    return await new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const MAX_WIDTH = 64
        const MAX_HEIGHT = 64
        const wRatio = MAX_WIDTH / img.width
        const hRatio = MAX_HEIGHT / img.height
        let width, height
        if (wRatio > hRatio) {
          width = MAX_WIDTH
          height = wRatio * img.height
        } else {
          width = hRatio * img.width
          height = MAX_HEIGHT
        }
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        ctx.filter = 'blur(1px)'
        ctx.drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.25))
      }
      img.onerror = (err) => {
        resolve('')
      }
      img.src = url
    })
  }

  return handler(options)
  `
  return new Function(fn) as unknown as Input['onChange']
}

const showIfContent: any = {
  locale: (properties: any) => properties.get('visibilityBasedOn') === 'locale',
  experiment: (properties: any) => properties.get('visibilityBasedOn') === 'experiment',
}

export type visibilityOptions = any

export const visibilityInputs = (visibilityOptions: visibilityOptions) => {
  const criteria = Object.keys(visibilityOptions)

  const criteriaInput = {
    name: 'visibilityBasedOn',
    friendlyName: 'Visibility based on',
    type: 'string',
    enum: criteria,
  }

  return [
    criteriaInput,
    ...criteria.reduce((acc: visibilityOptions, criterion) => {
      const criterionOptions = visibilityOptions[criterion]
      let optionInputs = []
      if (criterionOptions === 'experiment') {
        optionInputs = [
          {
            name: 'experimentFlag',
            friendlyName: `Experiment Flag`,
            type: 'string',
            showIf: showIfContent[criterion],
          },
          {
            name: 'experimentVariant',
            friendlyName: `Experiment Variant`,
            type: 'string',
            showIf: showIfContent[criterion],
          },
          {
            name: 'showAsOff',
            friendlyName: 'Show Contents to users not in experiment?',
            type: 'boolean',
            defaultValue: false,
            helperText:
              'If the experiment is not rolled out, but you still want this content to show by default, i.e OFF in amplitude, then set to true, and label variant as control. This will make content show up for users in OFF and Control, which is typically the same experience.',
            showIf: showIfContent[criterion],
          },
        ]
      } else {
        optionInputs = criterionOptions.map((inputCriteria: any) => {
          return {
            name: `${criterion}_${inputCriteria}`,
            friendlyName: `hide on ${inputCriteria}`,
            type: 'boolean',
            showIf: showIfContent[criterion],
          }
        })
      }
      return [...acc, ...optionInputs]
    }, []),
  ]
}

/** @example { '@type': 'LocalizedValue', Default: 'Default value', 'en-US': 'English value', 'fr-CA': 'French value' } */
export type LocalizedValue = Partial<
  {
    '@type': string
    Default: string
  } & Record<LocaleCode, string>
>

export const getBuilderLocalizedValue = ({
  value,
  locale,
  noDefault = false,
}: {
  value: LocalizedValue
  locale: string
  noDefault?: boolean
}) => {
  if (value?.[locale as keyof LocalizedValue]) {
    return value[locale as keyof LocalizedValue] ?? ''
  }
  return noDefault ? '' : value.Default
}

export const processExternalBODiLinks = (data: BuilderContent | BuilderContentSDK) => {
  function replacer(_key: string, value: any) {
    if (typeof value === 'string') {
      return transformExternalBODiLinkInText(value)
    }
    return value
  }
  return JSON.parse(JSON.stringify(data, replacer))
}

export function processBuilderContent(
  content: BuilderContent | BuilderContentSDK | undefined,
  builderMode?: BuilderPreviewMode
) {
  if (!content) return undefined
  if (builderMode?.edit) return undefined // conflicts with Builder.io editor overrides
  const proxiedContent = proxyImages(content)
  const processedContent = processExternalBODiLinks(proxiedContent)
  return processedContent
}
